import { useEffect, useState } from "react";
import { AccountMenu } from "./AccountMenu";
import { useAuth } from "../context/AuthContext";
import type { AdminRole } from "../types/auth";

type ProfileSection = "account" | "bio" | "access";

const roleLabels: Record<AdminRole, string> = {
  global_admin: "Administrateur global",
  formation_admin: "Administrateur formation",
  trainer: "Formateur",
};

const sections: Array<{
  id: ProfileSection;
  href: string;
  label: string;
}> = [
  {
    id: "account",
    href: "#/profile/account",
    label: "Compte",
  },
  {
    id: "bio",
    href: "#/profile/bio",
    label: "Bio",
  },
  {
    id: "access",
    href: "#/profile/access",
    label: "Accès",
  },
];

function currentProfileSection(): ProfileSection {
  if (window.location.hash.startsWith("#/profile/bio")) {
    return "bio";
  }

  if (window.location.hash.startsWith("#/profile/access")) {
    return "access";
  }

  return "account";
}

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function formatClaims(claims: {
  globalAdmin: boolean;
  canCreateFormation: boolean;
  canCreateSession: boolean;
}): string {
  const values = [
    claims.globalAdmin ? "globalAdmin" : null,
    claims.canCreateFormation ? "canCreateFormation" : null,
    claims.canCreateSession ? "canCreateSession" : null,
  ].filter((value): value is string => value !== null);

  return values.length > 0 ? values.join(", ") : "Aucun claim global";
}

export function ProfileSettingsPage() {
  const { claims, isAdmin, profile, user } = useAuth();
  const [activeSection, setActiveSection] = useState<ProfileSection>(
    currentProfileSection,
  );
  const connectedLogin = profile?.email || user?.email || "Compte";

  useEffect(() => {
    function handleHashChange() {
      setActiveSection(currentProfileSection());
    }

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
    };
  }, []);

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Profil utilisateur</p>
          <h1>Paramètres du compte</h1>
        </div>
        <AccountMenu />
      </header>

      <div className="profile-layout">
        <aside className="profile-sidebar" aria-label="Navigation profil">
          {isAdmin ? (
            <a className="profile-back-link" href="#/admin">
              Retour administration
            </a>
          ) : null}
          <nav className="profile-nav">
            {sections.map((section) => (
              <a
                className={
                  activeSection === section.id
                    ? "profile-nav-link profile-nav-link-active"
                    : "profile-nav-link"
                }
                href={section.href}
                key={section.id}
              >
                {section.label}
              </a>
            ))}
          </nav>
        </aside>

        <section className="profile-main">
          {activeSection === "account" ? (
            <article className="panel">
              <p className="eyebrow">Compte</p>
              <h2>{connectedLogin}</h2>
              <dl className="details-list">
                <div>
                  <dt>Nom affiché</dt>
                  <dd>{profile?.displayName || "Non renseigné"}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{connectedLogin}</dd>
                </div>
                <div>
                  <dt>UID</dt>
                  <dd className="mono-break">
                    {profile?.uid || user?.uid || "Non disponible"}
                  </dd>
                </div>
                <div>
                  <dt>Dernière connexion</dt>
                  <dd>{formatDate(profile?.lastLoginAt ?? null)}</dd>
                </div>
              </dl>
            </article>
          ) : null}

          {activeSection === "bio" ? (
            <article className="panel">
              <p className="eyebrow">Bio</p>
              <h2>{profile?.displayName || connectedLogin}</h2>
              <dl className="details-list">
                <div>
                  <dt>Rôle</dt>
                  <dd>{profile ? roleLabels[profile.role] : "Profil à compléter"}</dd>
                </div>
                <div>
                  <dt>Type de compte</dt>
                  <dd>{profile?.accountType ?? "Non renseigné"}</dd>
                </div>
                <div>
                  <dt>Statut</dt>
                  <dd>{profile?.status ?? "Non renseigné"}</dd>
                </div>
                <div>
                  <dt>Profil créé</dt>
                  <dd>{formatDate(profile?.createdAt ?? null)}</dd>
                </div>
                <div>
                  <dt>Dernière mise à jour</dt>
                  <dd>{formatDate(profile?.updatedAt ?? null)}</dd>
                </div>
              </dl>
            </article>
          ) : null}

          {activeSection === "access" ? (
            <article className="panel">
              <p className="eyebrow">Accès</p>
              <h2>Droits et capacités</h2>
              <dl className="details-list">
                <div>
                  <dt>Rôle</dt>
                  <dd>{profile ? roleLabels[profile.role] : "Profil à compléter"}</dd>
                </div>
                <div>
                  <dt>Custom claims</dt>
                  <dd>{formatClaims(claims)}</dd>
                </div>
                <div>
                  <dt>Créer formation</dt>
                  <dd>{profile?.capabilities.canCreateFormation ? "Oui" : "Non"}</dd>
                </div>
                <div>
                  <dt>Créer session</dt>
                  <dd>{profile?.capabilities.canCreateSession ? "Oui" : "Non"}</dd>
                </div>
                <div>
                  <dt>Inviter admin</dt>
                  <dd>{profile?.capabilities.canInviteAdmin ? "Oui" : "Non"}</dd>
                </div>
                <div>
                  <dt>Administrer comptes</dt>
                  <dd>{profile?.capabilities.canManageAdmins ? "Oui" : "Non"}</dd>
                </div>
              </dl>
            </article>
          ) : null}
        </section>
      </div>
    </main>
  );
}
